import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { BlogMetadataRecord } from 'api/clients/BlogMetadataRecordsClient'

export interface UseBlogMetadataCategoriesOptions {
  initialData?: BlogMetadataRecord
  onCategoriesUpdate?: (categories: string[]) => void
}

export interface BlogMetadataCategoriesState {
  selectedCategories: string[]
}

export interface BlogMetadataCategoriesHandlers {
  handleCategoriesChange: (newCategories: string[]) => void
  handleCategoryDropdownClose: (selectedCats: string[], updatedAvailable: string[]) => void
}

export interface UseBlogMetadataCategoriesReturn {
  categoriesState: BlogMetadataCategoriesState
  handlers: BlogMetadataCategoriesHandlers
}

/**
 * Hook to manage BlogMetadata categories
 * Generic hook that works with both TagInput and CategoryDropdown
 */
export const useBlogMetadataCategories = ({
  initialData,
  onCategoriesUpdate,
}: UseBlogMetadataCategoriesOptions): UseBlogMetadataCategoriesReturn => {
  // Memoize initialData values to prevent unnecessary re-renders
  const initialCategories = useMemo(() => initialData?.categories ?? [], [initialData?.categories])
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialCategories)

  // Track if we've synced with initialData to avoid overwriting user changes
  const hasSyncedRef = useRef(!!initialData)

  // Sync state when initialData becomes available (loading -> loaded)
  useEffect(() => {
    if (initialData && !hasSyncedRef.current) {
      if (initialData?.categories && initialData.categories.length > 0) {
        setSelectedCategories(initialData.categories)
      }
      hasSyncedRef.current = true
    }
  }, [initialData])

  // Handlers
  const handleCategoriesChange = useCallback(
    (newCategories: string[]) => {
      setSelectedCategories(newCategories)
      onCategoriesUpdate?.(newCategories)
    },
    [onCategoriesUpdate],
  )

  const handleCategoryDropdownClose = useCallback(
    (selectedCats: string[], updatedAvailable: string[]) => {
      setSelectedCategories(selectedCats)
      onCategoriesUpdate?.(selectedCats)
      // Note: updatedAvailable contains newly added categories
      // The parent (composition hook) will handle persistence
    },
    [onCategoriesUpdate],
  )

  return {
    categoriesState: {
      selectedCategories,
    },
    handlers: {
      handleCategoriesChange,
      handleCategoryDropdownClose,
    },
  }
}
